import { useCallback, useMemo, useRef } from 'react'
import { Layout, Responsive, WidthProvider } from 'react-grid-layout'
import { WidgetContainer } from '@/routes/Dashboard/components/WidgetContainer'

import { Widget, WidgetLayout } from '../types'

const ResponsiveGridLayout = WidthProvider(Responsive)

interface DashboardGridProps {
  widgets: Widget[]
  layouts: WidgetLayout[]
  onLayoutChange: (layouts: Layout[]) => void
  isEditMode: boolean
  filters: {
    introducingBrokerIds?: string
    countryIds?: string
    customDateRange?: [string, string]
  } | null
  onRemove: (widgetId: string) => void
}

export const DashboardGrid: React.FC<DashboardGridProps> = ({
  widgets,
  layouts,
  onLayoutChange,
  isEditMode,
  filters,
  onRemove,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const rglLayouts = useMemo(() => {
    return layouts.map((l) => ({
      i: l.i,
      x: l.x,
      y: l.y,
      w: l.w,
      h: l.h,
      minW: l.minW,
      minH: l.minH,
      static: l.static || false,
    }))
  }, [layouts])

  const handleRemove = useCallback(
    (id: string) => {
      onRemove(id)
    },
    [onRemove]
  )

  const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }
  const cols = { lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }

  const ROW_HEIGHT = 60
  const MARGIN = 16

  const cellWidth = 88
  const cellHeight = ROW_HEIGHT + MARGIN

  return (
    <div
      ref={containerRef}
      className={`p-8 relative rounded-xl 
  ${isEditMode ? 'dashboard-grid-lines' : ''}
  overflow-visible h-full
`}
      style={
        {
          '--cell-width': `${cellWidth}px`,
          '--cell-height': `${cellHeight}px`,
        } as React.CSSProperties
      }
    >
      {!rglLayouts || rglLayouts.length === 0 ? (
        <div className='text-center text-gray-500 py-10'>
          Add widgets by clicking "Manage Widgets" button
        </div>
      ) : (
        <ResponsiveGridLayout
          className='layout'
          layouts={{ lg: rglLayouts }}
          breakpoints={breakpoints}
          cols={cols}
          rowHeight={ROW_HEIGHT}
          margin={[MARGIN, MARGIN]}
          containerPadding={[0, 0]}
          draggableCancel='.no-drag'
          useCSSTransforms={true}
          compactType={null}
          preventCollision={true}
          isDraggable={isEditMode}
          isResizable={isEditMode}
          onLayoutChange={onLayoutChange}
        >
          {widgets.map((widget) => (
            <div
              key={widget.id}
              className='
              rounded-lg border border-black/10 shadow-sm
              transition-shadow duration-200
            '
              style={{ height: '100%' }}
            >
              <WidgetContainer
                widget={widget}
                widgetId={widget.id}
                isEditMode={isEditMode}
                filters={filters}
                onRemove={handleRemove}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      )}
    </div>
  )
}
